import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CustomerService } from '../../../Services/customer.service';
import { Customer, CustomerContactPerson } from '../../../Models/customer.model';
import { AuthService } from '../../../api-authorization/authorize.service';

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.css']
})
export class CustomerFormComponent {
  customer: Partial<Customer>;
  isEdit: boolean;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<CustomerFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Customer | null,
    private customerService: CustomerService,
    private authService: AuthService
  ) {
    this.isEdit = !!data?.id;
    this.customer = data ? { ...data } : {
      name: '',
      type: 'Normal',
      address: '',
      contactPersonName: '',
      contactPersonPhone: '',
      contactPersonEmail: '',
      invoiceName: '',
      invoiceAddress: '',
      shippingName: '',
      shippingAddress: '',
      customerNumber: '',
      vatId: '',
      orderNumber: '',
      orderDate: null,
      projectNumber: '',
      projectManagerName: '',
      projectManagerPhone: '',
      projectManagerEmail: '',
      invoiceEmail: '',
      paymentTerms: '',
      deliveryTerms: '',
      invoiceNote: '',
      contactPersons: []
    };
    this.customer.contactPersons = (this.customer.contactPersons ?? []).map((contact, index) => ({
      ...this.createEmptyContact(index),
      ...contact,
      sortOrder: contact.sortOrder ?? index
    }));
    if (this.customer.orderDate) {
      this.customer.orderDate = new Date(this.customer.orderDate).toISOString().slice(0, 10);
    }
  }

  /** Nur der SuperAdmin darf die Kundenart wählen bzw. einen Stammkunden anlegen. */
  get canChooseCustomerType(): boolean {
    return this.authService.canManageStammkunden();
  }

  get contactPersons(): CustomerContactPerson[] {
    if (!this.customer.contactPersons) {
      this.customer.contactPersons = [];
    }
    return this.customer.contactPersons;
  }

  addContactPerson(): void {
    this.contactPersons.push(this.createEmptyContact(this.contactPersons.length));
  }

  removeContactPerson(index: number): void {
    this.contactPersons.splice(index, 1);
    this.reindexContacts();
  }

  applyContactToInvoice(contact: CustomerContactPerson): void {
    this.customer.invoiceName = contact.fullName || this.customer.invoiceName || '';
    this.customer.invoiceAddress = contact.address || this.customer.invoiceAddress || this.customer.address || '';
    this.customer.invoiceEmail = contact.email || this.customer.invoiceEmail || '';
  }

  applyContactToShipping(contact: CustomerContactPerson): void {
    this.customer.shippingName = contact.fullName || this.customer.shippingName || '';
    this.customer.shippingAddress = contact.address || this.customer.shippingAddress || this.customer.invoiceAddress || this.customer.address || '';
  }

  applyContactToProject(contact: CustomerContactPerson): void {
    this.customer.projectManagerName = contact.fullName || this.customer.projectManagerName || '';
    this.customer.projectManagerPhone = contact.phone || this.customer.projectManagerPhone || '';
    this.customer.projectManagerEmail = contact.email || this.customer.projectManagerEmail || '';
  }

  copyBaseAddressToInvoice(): void {
    this.customer.invoiceName = this.customer.invoiceName || this.customer.name || '';
    this.customer.invoiceAddress = this.customer.invoiceAddress || this.customer.address || '';
  }

  copyInvoiceToShipping(): void {
    this.customer.shippingName = this.customer.shippingName || this.customer.invoiceName || this.customer.name || '';
    this.customer.shippingAddress = this.customer.shippingAddress || this.customer.invoiceAddress || this.customer.address || '';
  }

  save(): void {
    if (!this.customer.name?.trim()) return;
    this.saving = true;
    this.customer.contactPersons = this.contactPersons
      .filter(contact => contact.fullName?.trim())
      .map((contact, index) => ({
        ...contact,
        fullName: contact.fullName.trim(),
        phone: contact.phone?.trim() || '',
        email: contact.email?.trim() || '',
        address: contact.address?.trim() || '',
        sortOrder: index
      }));

    if (this.isEdit) {
      this.customerService.updateCustomer(this.customer.id!, this.customer as Customer).subscribe({
        next: () => { this.saving = false; this.dialogRef.close(true); },
        error: () => { this.saving = false; }
      });
    } else {
      this.customerService.createCustomer(this.customer as Customer).subscribe({
        next: () => { this.saving = false; this.dialogRef.close(true); },
        error: () => { this.saving = false; }
      });
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private createEmptyContact(sortOrder: number): CustomerContactPerson {
    return {
      fullName: '',
      phone: '',
      email: '',
      address: '',
      isInvoiceRecipient: false,
      isShippingRecipient: false,
      isProjectManager: false,
      isCommercialContact: false,
      isTechnicalContact: false,
      sortOrder
    };
  }

  private reindexContacts(): void {
    this.contactPersons.forEach((contact, index) => contact.sortOrder = index);
  }
}
